import Oidc from 'oidc-client';
import env from '../../config/env.json';

/**
 * OIDC configuration for the Swisscom CorpID service
 */
const mgr = new Oidc.UserManager({
  userStore: new Oidc.WebStorageStateStore({
    store: window.localStorage,
  }),
  authority:
    'https://sso-corproot-v2.scapp-services.swisscom.com/oauth/authorize',
  client_id: env.clientId,
  redirect_uri: env.redirectUri,
  response_type: 'token',
  scope: 'openid,roles,user_attributes',
  metadata: {
    authorization_endpoint:
      'https://sso-corproot-v2.scapp-services.swisscom.com/oauth/authorize',
    userinfo_endpoint:
      'https://sso-corproot-v2.scapp-services.swisscom.com/userinfo',
    jwks_uri: 'https://sso-corproot-v2.scapp-services.swisscom.com/token_keys',
    end_session_endpoint: `${window.location.protocol}//${window.location.host}`,
  },
});

Oidc.Log.logger = console;
Oidc.Log.level = Oidc.Log.INFO;

/**
 * Defines authentication methods for JWT authentication.
 */
export default class AuthenticationService {
  /**
   * Starts the oidc authentication process and redirects the user to the login screen.
   *
   * @returns empty promise.
   */
  static signinRedirect(): Promise<void> {
    return mgr.signinRedirect();
  }

  /**
   * Processes the response from the authorization endpoint to the callback route.
   *
   * @returns a promise which resolves to the logged in user.
   */
  static signinRedirectCallback(): Promise<Oidc.User> {
    return mgr.signinRedirectCallback();
  }

  /**
   * Get the user who is logged in.
   *
   * @returns a promise which resolves to either null or the user object.
   */
  static getUser(): Promise<Oidc.User | null> {
    return new Promise((resolve, reject) => {
      mgr
        .getUser()
        .then((user) => {
          if (user == null) {
            return resolve(null);
          } else {
            return resolve(user);
          }
        })
        .catch(function (err) {
          return reject(err);
        });
    });
  }

  /**
   * Check if there is any user logged in.
   *
   * @returns a promise which resolves to either true or false whether a user is signed in or not.
   */
  static getSignedIn(): Promise<boolean> {
    return new Promise((resolve, reject) => {
      mgr
        .getUser()
        .then(async (user) => {
          if (user == null) {
            return resolve(false);
          } else {
            return resolve(true);
          }
        })
        .catch(function (err) {
          return reject(err);
        });
    });
  }

  /**
   * Get the access token of the logged in user.
   *
   * @returns a promise which resolves to either null or the access token string.
   */
  static getAccessToken(): Promise<string | null> {
    return new Promise((resolve, reject) => {
      mgr
        .getUser()
        .then((user) => {
          if (user == null) {
            return resolve(null);
          } else {
            return resolve(user.access_token);
          }
        })
        .catch(function (err) {
          return reject(err);
        });
    });
  }

  /**
   * Checks if the saved access token is expired.
   *
   * @returns a promise which resolves to either true or false.
   */
  static isTokenExpired(): Promise<boolean> {
    return new Promise((resolve, reject) => {
      mgr
        .getUser()
        .then(function (user) {
          if (user == null) {
            return resolve(true);
          } else {
            return resolve(user.expired);
          }
        })
        .catch(function (err) {
          return reject(err);
        });
    });
  }
}
